/**
 * client/src/audio/airplay.ts — the client half of §6.6's `mk`, and the only thing in the product
 * that mints a credential into a URL.
 *
 * ⚠️ THE FAILURE THIS EXISTS TO PREVENT. When an iPhone hands a media element off to an Apple TV,
 * **the receiving device re-fetches the URL itself, with no access to Safari's cookie jar.** So the
 * artifact for a private or unlisted book answers 404 — correctly, by LAW 17 — and AirPlay simply
 * does nothing, with no error a user could act on and nothing in any log that says why. §6.6 takes
 * one bounded exception for exactly this case: `mk = HMAC(secret, actorKey‖pageId‖sha‖expMinute)`,
 * **one artifact, ten minutes, no other authority**, minted only by this path.
 *
 * P2 built the server half — `POST /api/v1/audio/mediakey`, `mintMediaKey`, `verifyMediaKey` — and
 * `docs/P2-STATUS.md` recorded the other half honestly: *"nothing in `client/src` ever mints a
 * key"*, so the machinery described a flow that was never invoked. This file is that flow.
 *
 * ── Three decisions, each of which is the difference between working and plausible ──────────────
 *
 *  1. **A `blob:` URL cannot be handed to a receiver at all.** §5.7 primes N+1 and N+2 into object
 *     URLs, which is what makes a page boundary silent on a locked phone — and an object URL names
 *     bytes inside *this* document. There is nothing for an Apple TV to fetch. So the handoff always
 *     re-points the element at the network URL, whatever it was holding; the canonical URL comes
 *     from `page.audio.url` (LAW 18: addressed by page, never by sha), never from `el.src`.
 *
 *  2. **We probe the way the receiver will fetch, instead of guessing.** A public book needs no key,
 *     and re-pointing its element would spend an audible reload to add a parameter that changes
 *     nothing. Rather than infer visibility from something the client was told, this asks the actual
 *     question with a two-byte `Range: bytes=0-1` and `credentials: "omit"` — which *is* the
 *     receiver's request. A 206 means the Apple TV can already fetch it and we leave the element
 *     alone. A 404 means it cannot, and only then do we mint.
 *
 *  3. **The minted URL is probed the same way before it replaces anything.** Swapping a working blob
 *     for a URL that turns out to 404 is a book that goes silent at the moment the user chose the
 *     living-room speakers. The probe costs two bytes and removes the whole class.
 *
 * ── Why a page-change subscription and not a one-shot ───────────────────────────────────────────
 * The engine assigns a fresh `src` at every page boundary — a blob if it managed to prime one — so a
 * handoff that fixed only the page playing at the time would break again ~110 seconds later, in the
 * middle of a chapter, on the device the user just walked away from. While the route is wireless
 * this controller follows `{type:"page"}` and re-points every page as it becomes current.
 *
 * ── The credential's hygiene ────────────────────────────────────────────────────────────────────
 * Keys are held in memory, never in `localStorage` (a ten-minute capability with a durable home is
 * not a ten-minute capability), never in an error message, never in a telemetry field.
 * `stripMediaKey()` is applied to anything this file reports, and the server's own log redaction is
 * the other half (`redactCredentials` in `server/src/routes/audio.ts`).
 *
 * Everything outside this module arrives through `AirPlayHost` and `AirPlayEngine`, so the whole
 * controller runs under `node --test` against the real Fastify route with no DOM — which is where it
 * is exercised (`test/range.test.ts`, the section named "the client half").
 */

import type { AudioLike, EngineEvent, EnginePage } from "./AudioEngine.ts";
import type { HttpInit } from "./pageWindow.ts";

// ─── the URL, and the credential in it ───────────────────────────────────────

/** `POST` here to mint. The one route in the API that answers with a credential. */
export const MEDIA_KEY_ROUTE = "/api/v1/audio/mediakey";

/**
 * `/api/v1/audio/page/c003.p07.m4a?v=<sha>` → its parts.
 *
 * Deliberately strict about the shape rather than reaching for `URL`: this runs on a string the
 * engine built from `pageAudioUrl()`, and anything else — an object URL, a `data:` URI, an absolute
 * URL at another origin — must fall out as `null` rather than be coerced into something we then
 * append a credential to. A credential appended to a foreign origin is a credential handed away.
 */
export function parsePageAudioUrl(url: string): { pageId: string; ext: string; v: string } | null {
  if (!url.startsWith("/api/v1/audio/page/")) return null;
  const q = url.indexOf("?");
  if (q < 0) return null;
  const name = url.slice("/api/v1/audio/page/".length, q);
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return null;
  const v = new URLSearchParams(url.slice(q + 1)).get("v");
  if (v === null || !/^[0-9a-f]{64}$/.test(v)) return null;
  return { pageId: decodeURIComponent(name.slice(0, dot)), ext: name.slice(dot + 1), v };
}

/**
 * Append (or replace) `&mk=`.
 *
 * Refuses anything `parsePageAudioUrl` does not recognise, and that refusal is the point: the one
 * place a credential may be written into a URL must be unable to write it into a different one.
 */
export function withMediaKey(url: string, mk: string): string {
  if (parsePageAudioUrl(url) === null) return url;
  return `${stripMediaKey(url)}&mk=${encodeURIComponent(mk)}`;
}

/**
 * The inverse, for anything this file is about to say out loud.
 *
 * §6.4: *"a capability in a log file is a capability with no expiry."* The server redacts its own
 * access log; a client that puts the same string into a console line, an error message or a
 * telemetry row has leaked it from the other end.
 */
export function stripMediaKey(url: string): string {
  return url.replace(/([?&])mk=[^&#]*&?/gi, "$1").replace(/[?&]$/, "");
}

/** Two artifacts are the same bytes when they agree on both. The key's scope is exactly this pair. */
const artifactKey = (pageId: string, v: string): string => `${pageId}\u0000${v}`;

// ─── the numbers ─────────────────────────────────────────────────────────────

/**
 * Re-mint when less than this is left.
 *
 * The server's TTL is ten minutes and a page is ~110 seconds of audio, so a key normally outlives
 * the page it was minted for several times over. This margin exists for the other case: a listener
 * who pauses on an Apple TV for eight minutes and presses play, whose element then re-buffers
 * against a key that expired while nobody was looking. The symptom would be "AirPlay works and then
 * stops", which is the hardest kind of report to act on.
 */
export const RENEW_BEFORE_MS = 2 * 60_000;

/**
 * The receiver's request, as a header. Two bytes.
 *
 * It is a **range** probe and not a `HEAD` on purpose: iOS opens an `<audio>` with exactly this, and
 * §6.6 makes answering it with a 206 mandatory — a 200 here means the resource will not be treated
 * as seekable, which breaks the scrubber and the handoff itself. So the probe that tells us whether
 * a key is needed also tells us whether the seekable contract still holds, on the one path where it
 * matters and the one platform we cannot test on.
 */
const PROBE_RANGE = "bytes=0-1";

/** How long we wait for `loadedmetadata` before restoring the clock anyway. */
const REPOINT_TIMEOUT_MS = 8_000;

// ─── the outside world ───────────────────────────────────────────────────────

/** The part of a `Response` this file reads. A real one satisfies it. */
export interface AirPlayResponse {
  readonly ok: boolean;
  readonly status: number;
  json(): Promise<unknown>;
}

/** Everything outside this module that is not the engine. Injected so this is testable with no DOM. */
export interface AirPlayHost {
  fetch(input: string, init?: HttpInit): Promise<AirPlayResponse>;
  now(): number;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(token: unknown): void;
}

/**
 * The part of `AudioEngine` this controller uses — three members, all already public.
 *
 * Written as a structural port rather than importing the class so that nothing here can reach into
 * the engine's decks, its window or its private state. The engine satisfies it by construction; if
 * one of these three ever changes shape, this file stops compiling, which is the point.
 */
export interface AirPlayEngine {
  readonly el: AudioLike;
  readonly currentPage: EnginePage | null;
  subscribe(fn: (ev: EngineEvent) => void): () => void;
}

/**
 * The WebKit-only half of a media element. Absent everywhere except Safari, which is the only place
 * AirPlay exists — so every use is guarded rather than assumed.
 */
export interface WirelessTarget {
  webkitCurrentPlaybackTargetIsWireless?: boolean;
  webkitShowPlaybackTargetPicker?(): void;
  addEventListener(type: string, handler: (ev?: unknown) => void): void;
  removeEventListener(type: string, handler: (ev?: unknown) => void): void;
}

/** What the reader can render: whether a route exists, and whether we are on it. */
export interface AirPlayState {
  /** An AirPlay target is on the network. Safari tells us; nothing else does. */
  available: boolean;
  /** Playback is currently going out over one. */
  wireless: boolean;
  /**
   * The server has no `AB_MEDIA_KEY_SECRET`, so no key can be minted at all (§6.6's named degraded
   * state). Public books still hand off; private ones cannot, and the UI may say so.
   */
  keysDisabled: boolean;
}

// ─── the key ring ────────────────────────────────────────────────────────────

interface HeldKey {
  mk: string;
  expiresAt: number;
}

/**
 * One live key per artifact, minted on demand and never persisted.
 *
 * **Single-flight per artifact.** A handoff and a page change can ask within the same tick, and two
 * mints for one artifact is two credentials where one was authorised — harmless but sloppy, and the
 * shape that becomes a mint storm the first time a retry loop is added above it.
 */
export class MediaKeyRing {
  readonly #held = new Map<string, HeldKey>();
  readonly #inFlight = new Map<string, Promise<HeldKey | null>>();
  #disabled = false;

  constructor(private readonly host: AirPlayHost) {}

  /** §6.6's degraded state: `AB_MEDIA_KEY_SECRET` is unset. Sticky, so we ask once and not per page. */
  get disabled(): boolean {
    return this.#disabled;
  }

  /** A key we already hold and that will still be valid for a while, or `null`. */
  peek(pageId: string, v: string): string | null {
    const held = this.#held.get(artifactKey(pageId, v));
    if (held === undefined) return null;
    if (held.expiresAt - this.host.now() <= RENEW_BEFORE_MS) return null;
    return held.mk;
  }

  /**
   * A live key, minting one if we do not hold a fresh one.
   *
   * `null` covers every refusal without distinguishing them to the caller, because there is exactly
   * one thing to do about all of them: leave the element alone and let the local session keep
   * playing. A 404 means this actor may not fetch that artifact (LAW 17 — the same answer as "no
   * such page", deliberately); a 403 means there is no session to bind a key to, which is the
   * anonymous-on-a-public-book case that needs no key anyway; a 503 means the server has no secret.
   */
  ensure(pageId: string, v: string): Promise<HeldKey | null> {
    const key = artifactKey(pageId, v);
    const held = this.#held.get(key);
    if (held !== undefined && held.expiresAt - this.host.now() > RENEW_BEFORE_MS) {
      return Promise.resolve(held);
    }
    if (this.#disabled) return Promise.resolve(null);

    const already = this.#inFlight.get(key);
    if (already !== undefined) return already;

    const flight = this.#mint(pageId, v)
      .then((minted) => {
        if (minted !== null) this.#held.set(key, minted);
        return minted;
      })
      .finally(() => this.#inFlight.delete(key));
    this.#inFlight.set(key, flight);
    return flight;
  }

  async #mint(pageId: string, v: string): Promise<HeldKey | null> {
    let res: AirPlayResponse;
    try {
      res = await this.host.fetch(MEDIA_KEY_ROUTE, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ pageId, v }),
        // The cookie is the whole authority here: the key is minted for the actor the session names,
        // and `POST` with no cookie is the 403 branch below.
        credentials: "same-origin",
      });
    } catch {
      // Offline, or the tab is being frozen. Not an error worth surfacing: without a key AirPlay
      // degrades to "public books only", which is exactly where it was before this file existed.
      return null;
    }

    if (res.status === 503) {
      // `media-key-disabled`. Asking again every page would be a request per two minutes, forever,
      // to be told the same thing.
      this.#disabled = true;
      return null;
    }
    if (!res.ok) return null;

    const body = (await res.json()) as { mk?: unknown; expiresAt?: unknown };
    if (typeof body.mk !== "string" || body.mk === "" || typeof body.expiresAt !== "number") return null;
    return { mk: body.mk, expiresAt: body.expiresAt };
  }

  /** Forget everything. Called when the route goes back to the local speakers, and on destroy. */
  clear(): void {
    this.#held.clear();
  }
}

// ─── the controller ──────────────────────────────────────────────────────────

export interface AirPlayOptions {
  /** Called whenever `available`, `wireless` or `keysDisabled` changes. */
  onChange?: (state: AirPlayState) => void;
}

/**
 * Watches the route, and keeps whatever the receiver is fetching fetchable.
 *
 * It owns no policy about *when* to hand off — that is the user tapping the route picker, and
 * WebKit's own UI. It owns exactly one thing: while the audio is going out over AirPlay, the URL on
 * the element is one the receiving device can actually fetch.
 */
export class AirPlayController {
  readonly #ring: MediaKeyRing;
  readonly #detach: (() => void)[] = [];

  /** artifact → "a cookieless fetch already works", so a public book is never re-pointed twice. */
  readonly #cookielessOk = new Map<string, boolean>();

  /**
   * The elements we have already put listeners on.
   *
   * ⚠️ **The engine ping-pongs two `<audio>` elements** (§5.7: two decks, created once at module
   * load, both unlocked in one gesture) and `engine.el` is whichever is *current*. Binding once at
   * attach would leave the other deck deaf — so the first page turn after a handoff would move the
   * route onto an element this controller had never heard of, and the wireless event would arrive at
   * nobody. Re-binding on every page event is idempotent and costs a Set lookup.
   */
  readonly #bound = new Set<AudioLike>();

  #available = false;
  #wireless = false;
  #destroyed = false;
  /** Bumped on every re-point, so a slow probe cannot assign a src for a page we have left. */
  #gen = 0;

  constructor(
    private readonly engine: AirPlayEngine,
    private readonly host: AirPlayHost,
    private readonly opts: AirPlayOptions = {},
  ) {
    this.#ring = new MediaKeyRing(host);
  }

  get state(): AirPlayState {
    return { available: this.#available, wireless: this.#wireless, keysDisabled: this.#ring.disabled };
  }

  /**
   * Start watching. Returns the detach.
   *
   * ⚠️ **Both events are needed and they mean different things.**
   * `webkitplaybacktargetavailabilitychanged` fires when an AirPlay device appears on the network —
   * i.e. when the *button* should appear. `webkitcurrentplaybacktargetiswirelesschanged` fires when
   * playback actually moves. Only the second one is the moment a receiver starts fetching, so it is
   * the only one that re-points; the first one exists so the reader can render the button, and so a
   * key can be warmed before it is needed rather than in the half-second the user is watching.
   */
  attach(): () => void {
    this.#bind(this.engine.el);

    // The page the engine turns to next is assigned by the engine, without us, and a blob is exactly
    // what it will prefer — so every page has to be re-pointed as it becomes current, not just the
    // one that was playing when the user chose the Apple TV. It is also where the deck may have
    // swapped, which is why the binding is refreshed here and not only above.
    this.#detach.push(
      this.engine.subscribe((ev) => {
        if (ev.type !== "page") return;
        this.#bind(this.engine.el);
        if (this.#wireless) void this.#repointCurrent();
      }),
    );

    // Safari may already be on a route when this mounts — a reload while playing to an Apple TV.
    if ((this.engine.el as unknown as WirelessTarget).webkitCurrentPlaybackTargetIsWireless === true) {
      this.#wireless = true;
      this.#available = true;
      this.#emit();
      void this.#repointCurrent();
    }

    return () => this.destroy();
  }

  /** Put the two WebKit route listeners on one element, once. */
  #bind(target: AudioLike): void {
    if (this.#bound.has(target)) return;
    this.#bound.add(target);
    const el = target as unknown as WirelessTarget;

    const onAvailability = (ev?: unknown): void => {
      const availability = (ev as { availability?: string } | undefined)?.availability;
      // The event carries the answer; the element does not expose it as a property. An event with no
      // `availability` field is treated as "available", because it fired at all.
      this.#setAvailable(availability === undefined ? true : availability === "available");
      if (this.#available) void this.#warm();
    };
    const onWireless = (): void => {
      const now = el.webkitCurrentPlaybackTargetIsWireless === true;
      if (now === this.#wireless) return;
      this.#wireless = now;
      this.#emit();
      if (now) void this.#repointCurrent();
      // Going back to the local speakers deliberately does NOT re-point. The URL carrying an expired
      // `mk` still serves from this browser — the cookie was always sufficient here — so tearing the
      // element down to remove a parameter would cost an audible gap to change nothing.
      else this.#ring.clear();
    };

    el.addEventListener("webkitplaybacktargetavailabilitychanged", onAvailability);
    el.addEventListener("webkitcurrentplaybacktargetiswirelesschanged", onWireless);
    this.#detach.push(() => {
      el.removeEventListener("webkitplaybacktargetavailabilitychanged", onAvailability);
      el.removeEventListener("webkitcurrentplaybacktargetiswirelesschanged", onWireless);
    });
  }

  /** Open WebKit's own route picker. The reader's AirPlay button calls this; there is no other way. */
  showPicker(): boolean {
    const el = this.engine.el as unknown as WirelessTarget;
    if (typeof el.webkitShowPlaybackTargetPicker !== "function") return false;
    el.webkitShowPlaybackTargetPicker();
    return true;
  }

  destroy(): void {
    this.#destroyed = true;
    this.#gen++;
    for (const off of this.#detach.splice(0)) off();
    this.#ring.clear();
  }

  // ── the work ───────────────────────────────────────────────────────────────

  #setAvailable(next: boolean): void {
    if (next === this.#available) return;
    this.#available = next;
    this.#emit();
  }

  #emit(): void {
    this.opts.onChange?.(this.state);
  }

  /**
   * A route exists but nothing has moved yet. Mint ahead, so the handoff itself is one probe and an
   * assignment rather than a round trip to cloud-2 while the user watches the spinner.
   */
  async #warm(): Promise<void> {
    const page = this.engine.currentPage;
    if (page === null || this.#destroyed) return;
    const { pageId, artifactSha, url } = page.audio;
    if ((await this.#reachableWithoutCookie(url, pageId, artifactSha)) !== false) return;
    await this.#ring.ensure(pageId, artifactSha);
  }

  /**
   * Make the current page fetchable by the receiver, or leave it alone.
   *
   * The order is the design: **ask whether a key is needed before minting one, and prove the keyed
   * URL works before assigning it.** Each step can only reduce what happens; none of them can leave
   * the element holding something worse than it had.
   */
  async #repointCurrent(): Promise<void> {
    const gen = ++this.#gen;
    const page = this.engine.currentPage;
    if (page === null || this.#destroyed) return;
    const { pageId, artifactSha, url } = page.audio;

    const el = this.engine.el;
    // Already on the network URL for this artifact, with a key we still hold: nothing to do. This is
    // the common case on a second `{type:"page"}` for the same page (a re-open after a font change).
    const parsed = parsePageAudioUrl(el.src);
    if (parsed !== null && parsed.pageId === pageId && parsed.v === artifactSha && /[?&]mk=/.test(el.src)) return;

    const needsKey = await this.#reachableWithoutCookie(url, pageId, artifactSha);
    if (gen !== this.#gen || this.#destroyed) return;
    if (needsKey === null) return; // the probe itself failed — do not touch a playing element on a guess
    if (needsKey === false) {
      // A public book. The receiver can already fetch this URL, so the only thing that could be wrong
      // is the element holding a `blob:` — which no receiver can resolve.
      if (el.src.startsWith("blob:")) this.#assign(el, url, gen);
      return;
    }

    const key = await this.#ring.ensure(pageId, artifactSha);
    if (key === null || gen !== this.#gen || this.#destroyed) return;

    const keyed = withMediaKey(url, key.mk);
    // The same two-byte question, now of the URL we are about to commit to. Swapping a working blob
    // for a 404 is a book that goes silent at the exact moment the user chose the living-room
    // speakers, and it would look like the Apple TV's fault.
    if (!(await this.#probeCookieless(keyed))) return;
    if (gen !== this.#gen || this.#destroyed) return;

    this.#assign(el, keyed, gen);
  }

  /**
   * `true` — a key is needed. `false` — the receiver can already fetch it. `null` — we could not ask.
   *
   * Cached per artifact: the answer is a property of the book's visibility, and re-asking on every
   * page turn would spend a request per page to learn the same thing. A book taken private mid-listen
   * invalidates this — and is handled by the keyed fetch failing, which is the same 404 the receiver
   * would get and is exactly what §6.4's `revalidateRooms` is for on the other transport.
   */
  async #reachableWithoutCookie(url: string, pageId: string, v: string): Promise<boolean | null> {
    const key = artifactKey(pageId, v);
    const known = this.#cookielessOk.get(key);
    if (known !== undefined) return !known;
    const ok = await this.#probeCookieless(url);
    if (ok === null) return null;
    this.#cookielessOk.set(key, ok);
    return !ok;
  }

  /**
   * The receiver's own request: two bytes, no cookie.
   *
   * `credentials: "omit"` is the entire experiment. With `same-origin` this would always succeed for
   * the reader and tell us nothing about the device that is about to fetch it.
   */
  async #probeCookieless(url: string): Promise<boolean | null> {
    try {
      const res = await this.host.fetch(url, {
        method: "GET",
        headers: { range: PROBE_RANGE },
        credentials: "omit",
      });
      // 206 is the only affirmative answer worth having. A 200 to a Range request means the resource
      // will not be treated as seekable (§6.6), which breaks the handoff for a different reason —
      // so it is not counted as reachable and the keyed URL is tried instead.
      return res.status === 206;
    } catch {
      return null;
    }
  }

  /**
   * Assign a new `src` while keeping the listener's place.
   *
   * ⚠️ This is the one place in the product that re-points a *playing* element, and it is allowed
   * here for the reason §4.11 forbids it elsewhere: the alternative is not a small gap, it is
   * silence on the device the user just chose. The handoff itself already costs a second or two of
   * buffering, so the reload hides inside a pause the user is expecting.
   *
   * The clock is restored by seconds and not by sid, deliberately and unusually (LAW 3 says the
   * opposite everywhere else): this is the *same bytes* at the *same artifact sha*, so second `n` is
   * second `n` — there is no axis change to migrate across, and going through a sid would round to a
   * sentence boundary and repeat half a sentence out loud in the living room.
   */
  #assign(el: AudioLike, src: string, gen: number): void {
    const at = el.currentTime;
    const wasPlaying = !el.paused;

    let settled = false;
    const restore = (): void => {
      if (settled) return;
      settled = true;
      el.removeEventListener("loadedmetadata", restore);
      this.host.clearTimer(timer);
      if (gen !== this.#gen || this.#destroyed) return;
      if (at > 0) el.currentTime = at;
      if (wasPlaying) {
        void Promise.resolve(el.play()).catch(() => {
          /* Autoplay refused after a route change is the engine's `needs-gesture` story, not ours. */
        });
      }
    };
    const timer = this.host.setTimer(restore, REPOINT_TIMEOUT_MS);

    el.addEventListener("loadedmetadata", restore);
    el.src = src;
    el.load();
  }
}

// ─── the browser ─────────────────────────────────────────────────────────────

/** The real browser, or `null` under `node --test` and any other DOM-less host. */
export function browserAirPlayHost(): AirPlayHost | null {
  if (typeof window === "undefined" || typeof fetch === "undefined") return null;
  return {
    fetch: (input, init) => fetch(input, init as RequestInit),
    now: () => Date.now(),
    setTimer: (fn, ms) => window.setTimeout(fn, ms),
    clearTimer: (token) => window.clearTimeout(token as number),
  };
}

/**
 * Wire an engine to the route. **Called once, from `AudioEngine.ts`'s singleton** — where the two
 * `<audio>` elements are made, rather than from a React tree that unmounts on navigation.
 * `test/range.test.ts` asserts that call exists, because a module with a full test section and no
 * caller is the exact shape of the failure `docs/P3-STATUS.md` opens with.
 *
 * Returns `null` where there is no browser, so importing this module under Node is safe and every
 * `new AudioEngine(fakeHost)` in the suite is untouched.
 *
 * ⚠️ **Still not wired: the button.** `showPicker()` is the only way to open WebKit's route picker
 * from our own UI, and no reader control calls it yet. That is a smaller gap than it sounds — iOS
 * offers the route picker from Control Center and the lock screen for whatever is playing, which is
 * the path a listener actually uses — but a visible AirPlay button in the reader is one `onClick`
 * away and belongs to whoever owns the player chrome.
 */
export function installAirPlay(engine: AirPlayEngine, opts: AirPlayOptions = {}): AirPlayController | null {
  const host = browserAirPlayHost();
  if (host === null) return null;
  const controller = new AirPlayController(engine, host, opts);
  controller.attach();
  return controller;
}
